/**
 * Form Container
 */
import { Component, registerComponent } from 'js/component.js';
import { Form, REGIONS } from '@RockwellAutomation/rockwell-meteor';
const componentName = 'form-container-meteor';
const KNOWN_USER_COOKIE_MAX_AGE = 90 * 24 * 60 * 60;

export class FormContainer extends Component {
    /**
     * Component Setup
     */
    // Elements
    elements = {
        form: this.element.querySelector('form'),
        defaultIndustryOptions: this.element.querySelector(
            '.px-default-options--industry',
        ),
        defaultCountryOptions: this.element.querySelector(
            '.px-default-options--country',
        ),
        defaultHelpOptions: this.element.querySelector(
            '.px-default-options--help',
        ),
        defaultPreferredLanguageOptions: this.element.querySelector(
            '.px-default-options--preferred-language',
        ),
        defaultStateOptions: this.element.querySelector(
            '.px-default-options--state',
        ),
        industrySelect: this.element.querySelector('select[name="industry1"]'),
        preferredLanguageSelect: this.element.querySelector(
            'select[name="preferredLanguage1"]',
        ),
        countrySelect: this.element.querySelector('select[name="country"]'),
        stateSelect: this.element.querySelector('select[name="stateProv"]'),
        helpSelect: this.element.querySelector(
            'select[name="QualifyingQuestions"]',
        ),
        submitButton: this.element.querySelector('button[type="submit"]'),
    };

    // Util & State
    state = {
        theme: this.elements.form.getAttribute('data-theme'),
        digitalData: window.digitalData || [],
        formName: this.elements.form.getAttribute('data-name') || 'default',
        formId: this.elements.form.getAttribute('data-id') || 'default',
        formTitle: this.elements.form.getAttribute('data-title') || 'default',
        formType: this.elements.form.getAttribute('data-type') || 'default',
        formAction: this.elements.form.getAttribute('action'),
        redirectPage: this.elements.form.getAttribute('data-redirect'),
        authorable: this.elements.form.getAttribute('data-authorable'),
        stateInputType: 'select',
        siteCountry: document.documentElement.lang.split('-')[1] ? document.documentElement.lang.split('-')[1].toLowerCase() : 'us',
    };

    /**
     * Extract hostname from URL
     */
    getHostName = (url) => {
        try {
            if (typeof window.URL === 'function') {
                return new window.URL(url).hostname;
            }
            const match = url.match(/:\/\/(www[0-9]?\.)?(.[^/:]+)/i);
            if (match !== null && match.length > 2 && typeof match[2] === 'string' && match[2].length > 0) {
                return match[2];
            }
            return null;

        } catch (error) {
            return null;
        }
    };

    /**
     * Extract Eloqua Site ID from existing hidden input
     */
    extractEloquaSiteId = () => {
        // Get from existing elqSiteID hidden input (rendered by model.hiddenInputs)
        const siteIdInput = this.elements.form.querySelector('input[name="elqSiteID"]');
        if (siteIdInput && siteIdInput.value) {
            return siteIdInput.value;
        }

        // Fallback: extract from form action URL pattern
        const { formAction } = this.state;
        if (formAction) {
            const match = formAction.match(/s(\d+)\.t\.eloqua\.com/);
            if (match && match[1]) {
                return match[1];
            }
        }

        return null;
    };

    /**
     * Get Eloqua Form Submission Token
     */
    getEloquaFormSubmissionToken = async () => {
        try {
            const siteId = this.extractEloquaSiteId();
            if (!siteId) {
                return;
            }

            const { formAction } = this.state;
            const hostname = this.getHostName(formAction);

            if (!hostname) {
                return;
            }

            const tokenUrl = `https://${hostname}/e/formsubmittoken?elqSiteID=${siteId}`;
            const tokenInput = this.elements.form.querySelector('#elqFormSubmissionToken');

            if (!tokenInput) {
                return;
            }

            const response = await fetch(tokenUrl, {
                method: 'GET',
                mode: 'cors',
            });

            if (response.ok) {
                const token = await response.text();
                tokenInput.value = token;
            } else {
                tokenInput.value = '';
            }
        } catch (error) {
            const tokenInput = this.elements.form.querySelector('#elqFormSubmissionToken');
            if (tokenInput) {
                tokenInput.value = '';
            }
        }
    };

    /**
     * Send Analytics View
     */
    sendAnalyticsView = () => {
        this.state.digitalData.push({
            event: 'formView',
            formViewDetail: {
                formName: this.state.formName,
                formId: this.state.formId,
                formTitle: this.state.formTitle,
                formType: this.state.formType,
            },
        });
    };

    /**
     * Send Analytics Submit
     */
    sendAnalyticsSubmit = () => {
        this.state.digitalData.push({
            event: 'formSubmit',
            formSubmitDetail: {
                formName: this.state.formName,
                formId: this.state.formId,
                formTitle: this.state.formTitle,
                formType: this.state.formType,
            },
        });
    };

    /**
     * Remember successful Plex lead-form submissions for the Akamai ID wall.
     * The host-only cookie works on both production and non-production hosts.
     */
    setKnownUserCookie = () => {
        document.cookie = `plexKnownUser=v1; Max-Age=${KNOWN_USER_COOKIE_MAX_AGE}; Path=/; Secure; SameSite=Lax`;
    };

    /**
     * Handle File Uploads and Submit the Form
     */
    handleFileUploadsAndSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData(this.elements.form);

        if (this.elements.stateSelect) {
            formData.delete('stateProv');
            if (this.state.stateInputType === 'select') {
                const stateSelect = this.elements.stateSelect.value;
                formData.append('stateProv', stateSelect);
            }
        }

        // Check for UTM parameters in query string
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('utm_campaign')) {
            formData.append('utm_campaign', urlParams.get('utm_campaign'));
        }
        if (urlParams.get('utm_creative')) {
            formData.append('utm_creative', urlParams.get('utm_creative'));
        }
        if (urlParams.get('utm_medium')) {
            formData.append('utm_medium', urlParams.get('utm_medium'));
        }
        if (urlParams.get('utm_placement')) {
            formData.append('utm_placement', urlParams.get('utm_placement'));
        }
        if (urlParams.get('utm_source')) {
            formData.append('utm_source', urlParams.get('utm_source'));
        }
        if (urlParams.get('utm_content')) {
            formData.append('utm_content', urlParams.get('utm_content'));
        }
        if (urlParams.get('utm_term')) {
            formData.append('utm_term', urlParams.get('utm_term'));
        }

        // Show Loader
        window.dispatchEvent(new CustomEvent('show-loading', null));

        // Convert FormData to URL-encoded format for Eloqua compatibility.
        // Telephone fields are submitted with the selected country code
        // prepended exactly once (e.g. "+1" + "5551234567"). The country code
        // lives in a separate `--country-code` dropdown that is dropped from the
        // payload. The visible telephone field keeps only the user-entered
        // number — the Meteor engine's getFullTelephone is overridden in init()
        // so it no longer writes the combined value back into the field, which
        // is what previously caused the code to be prepended twice.
        const urlEncodedData = new URLSearchParams();
        Array.from(formData.entries()).forEach(([key, value]) => {
            if (!key.endsWith('--country-code')) {
                const input = this.elements.form.querySelector(`input[name="${key}"]`);
                if (input && input.type === 'tel') {
                    const countryCode = formData.get(`${key}--country-code`) || '';
                    urlEncodedData.append(key, `${countryCode} ${value}`);
                } else {
                    urlEncodedData.append(key, value);
                }
            }
        });

        // Make AJAX Call
        try {
            const response = await fetch(this.state.formAction, {
                method: 'POST',
                headers: {
                    'Content-Type':
                        'application/x-www-form-urlencoded; charset=UTF-8',
                },
                mode: 'no-cors',
                body: urlEncodedData,
            });

            if (
                response
                && (response.status === 200 || response.status === 0)
            ) {
                // Analytics
                this.sendAnalyticsSubmit();
                this.setKnownUserCookie();
                if (this.state.redirectPage && this.state.redirectPage.endsWith('.pdf')) {
                    this.enablePDFViewer(this.state.redirectPage);
                    window.dispatchEvent(new CustomEvent('hide-loading', null));
                } else {
                    window.location.href = this.state.redirectPage || window.location.href;
                }
            } else {
                window.dispatchEvent(new CustomEvent('hide-loading', null));
            }
        } catch (error) {
            console.warn(error);
            window.dispatchEvent(new CustomEvent('hide-loading', null));
        }
    };

    enablePDFViewer = (pdfURL) => {
        const pdfViewerDisabled = document.getElementById('pira-pdf-viewer-disabled');
        const pdfContainer = document.querySelector('.pdf-viewer__container');

        let isExternal = true;
        try {
            if (pdfURL.includes(window.location.hostname) || pdfURL.includes('rockwellautomation.com') || pdfURL.includes('sprecherschuh.com') || pdfURL.startsWith('/content/dam/')) {
                isExternal = false;
            }
        } catch {
            // do nothing
        }

        if (pdfViewerDisabled || !pdfContainer || isExternal) {
            window.open(pdfURL, '_blank');
            return;
        }

        // replace http links with https
        pdfURL = pdfURL.replace(/^http:/, 'https:'); // eslint-disable-line no-param-reassign

        document.getElementById('cmp-pdf-viewer-container').innerHTML = `<iframe class="pdf-viewer__iframe" src="/etc.clientlibs/rockwell-aem-base/components/content/pdf-viewer/clientlibs/site/resources/viewer.html?file=${pdfURL}" ></iframe>`;
        document.body.style.overflow = 'hidden';

        // open viewer
        setTimeout(() => {
            pdfContainer.style.display = 'block';
        }, 100);

        // intercept 'local/RA' PDF links within Viewer and also open in Viewer instead of new tab
        function iFrameClick() {
            pdfContainer.querySelector('.pdf-viewer__iframe').contentWindow.document.body.onclick = (ev) => {
                ev.preventDefault();
                const originalHref = ev.target.tagName === 'A' ? ev.target.getAttribute('href') : '';
                if (originalHref && !originalHref.startsWith('#')) {
                    if (originalHref.includes('.pdf') && originalHref.includes('rockwellautomation.com')) {
                        const pdfIframe = pdfContainer.querySelector('.pdf-viewer__iframe');
                        const originalHrefHttps = originalHref.replace(/^http:/, 'https:');
                        pdfIframe.src = `/etc.clientlibs/rockwell-aem-base/components/content/pdf-viewer/clientlibs/site/resources/viewer.html?file=${originalHrefHttps}`;
                    } else {
                        window.open(originalHref, '_blank');
                    }
                }
            };
        }

        function closeLogic() {
            pdfContainer.style.display = 'none';
            document.body.style.overflow = 'auto';
            document.getElementById('cmp-pdf-viewer-container').innerHTML = '';
            pdfContainer.removeEventListener('click', closeLogic);
            window.dispatchEvent(new CustomEvent('hide-loading'));
        }

        pdfContainer.addEventListener('click', closeLogic);
        pdfContainer.querySelector('.pdf-viewer__iframe').addEventListener('load', iFrameClick);
    };

    handleSubmit = (e) => {
        if (e.defaultPrevented) return;
        this.handleFileUploadsAndSubmit(e);
    };

    /**
     * Add Event Listeners
     */
    addEventListeners = () => {
        this.elements.form.addEventListener('submit', this.handleSubmit);
    };

    /**
     * Initialize Component
     */
    init() {
        this.sendAnalyticsView();
        this.addEventListeners();

        // Get Eloqua submission token
        this.getEloquaFormSubmissionToken();
        if (!this.state.authorable || this.state.formType == "custom") {
            const editableFormContainer = this.element.querySelector('.aem-Grid');
            const children = Array.from(editableFormContainer.children);
            const defaultFields = ["firstName", "lastName", "emailAddress", "businessPhone", "company", "title", "industry1", "preferredLanguage1", "country", "stateProv", "city", "zipPostal", "address1", "address2", "QualifyingQuestions", "message", "referral_source", "resubscribe", "canadianOptIn1", "gDPRConsent1"];
            children.forEach(child => {
                if ((child.querySelector('input') && defaultFields.includes(child.querySelector('input').getAttribute('name'))) ||
                    (child.querySelector('select') && defaultFields.includes(child.querySelector('select').getAttribute('name'))) ||
                    (child.querySelector('textarea') && defaultFields.includes(child.querySelector('textarea').getAttribute('name')))) {
                    child.remove();
                }
            })
        } else if (this.state.authorable && !(this.state.formType == "default" || this.state.formType == null)) {
            const formContainerChildren = this.elements.form.firstElementChild.children;
            Array.from(formContainerChildren).forEach((ele) => {
                if (ele.classList.contains('px-formfield') || ele.classList.contains('px-formfield-wrapper')) {
                    ele.remove();
                }
            });
        }
        else {
            const editableFormContainer = this.element.querySelector('.aem-Grid');
            editableFormContainer.remove();
        }

        // Set default values
        if (
            this.elements.countrySelect
            && this.elements.countrySelect.querySelectorAll('option').length <= 1
        ) {
            this.elements.defaultCountryOptions
                .querySelectorAll('option')
                .forEach((option) => {
                    this.elements.countrySelect.appendChild(option);
                    // Remove automatic country selection - country should default to empty
                });
        }
        this.elements.defaultCountryOptions.remove();

        if (
            this.elements.helpSelect
            && this.elements.helpSelect.querySelectorAll('option').length <= 1
        ) {
            this.elements.defaultHelpOptions
                .querySelectorAll('option')
                .forEach((option) => {
                    this.elements.helpSelect.appendChild(option);
                });
        }
        this.elements.defaultHelpOptions.remove();

        if (
            this.elements.industrySelect
            && this.elements.industrySelect.querySelectorAll('option').length <= 1
        ) {
            this.elements.defaultIndustryOptions
                .querySelectorAll('option')
                .forEach((option) => {
                    this.elements.industrySelect.appendChild(option);
                });
        }
        this.elements.defaultIndustryOptions.remove();

        if (
            this.elements.preferredLanguageSelect
            && this.elements.defaultPreferredLanguageOptions
            && this.elements.preferredLanguageSelect.querySelectorAll('option').length <= 1
        ) {
            this.elements.defaultPreferredLanguageOptions
                .querySelectorAll('option')
                .forEach((option) => {
                    this.elements.preferredLanguageSelect.appendChild(option);
                });
        }
        if (this.elements.defaultPreferredLanguageOptions) {
            this.elements.defaultPreferredLanguageOptions.remove();
        }

        // Build an English -> localized state-name lookup from the server-
        // rendered generic list, then drop the helper markup. The list is flat
        // (no country grouping), so on its own it can't be scoped per country;
        // we merge it below with the Meteor engine's country-keyed REGIONS to
        // get country-scoped AND localized state options.
        const localizedStateName = {};
        if (this.elements.defaultStateOptions) {
            this.elements.defaultStateOptions
                .querySelectorAll('option')
                .forEach((option) => {
                    const en = option.getAttribute('data-en');
                    if (en) {
                        localizedStateName[en] = option.textContent;
                    }
                });
            this.elements.defaultStateOptions.remove();
        }

        // Take over the state field from the Meteor engine. Its
        // stateConditionalRule targets `.${theme}-formfield--state`; stripping
        // that class before Form.init() makes the rule capture an undefined
        // target and become a permanent no-op, so it can't overwrite our
        // localized options with its English-only REGIONS. We re-add the class
        // afterwards purely for styling.
        const stateTheme = this.state.theme;
        const stateField = this.elements.stateSelect
            ? this.elements.stateSelect.closest(`.${stateTheme}-formfield`)
            : null;
        if (stateField) {
            stateField.classList.remove(`${stateTheme}-formfield--state`);
        }

        // Initialize Form Engine after DOM modifications are finalized
        this.formEngine = new Form(this.elements.form);
        this.formEngine.init();

        // The Meteor form engine's submit handler rewrites the telephone field
        // to `${countryCode} ${number}` (see getFullTelephone), which both
        // mutates the visible field and prepends the country code to the
        // submitted value. Override it to keep only the user-entered number;
        // the country code remains available in its own dropdown.
        if (typeof this.formEngine.getFullTelephone === 'function') {
            this.formEngine.getFullTelephone = () => this.formEngine.getFormField('telephone')?.getValue() || '';
        }

        // Reproduce the engine's country-driven behaviour, but with localized
        // option text. REGIONS is keyed by uppercase ISO country code (matching
        // the country field value) and lists each sub-region's English name and
        // submit value. We translate the name via the generic-list lookup,
        // falling back to the English name when no translation exists.
        if (stateField && this.elements.countrySelect && this.elements.stateSelect) {
            stateField.classList.add(`${stateTheme}-formfield--state`);
            const { stateSelect } = this.elements;
            const placeholder = stateSelect.querySelector('option[value=""]');
            const syncState = () => {
                const country = this.elements.countrySelect.value;
                const regions = (country && REGIONS[country]) || null;
                stateSelect.innerHTML = '';
                if (placeholder) {
                    stateSelect.appendChild(placeholder.cloneNode(true));
                }
                if (regions && regions.length) {
                    regions.forEach((region) => {
                        const option = document.createElement('option');
                        option.value = region.value;
                        option.textContent = localizedStateName[region.name] || region.name;
                        stateSelect.appendChild(option);
                    });
                    stateField.classList.remove(`${stateTheme}-u-hidden`);
                } else {
                    stateField.classList.add(`${stateTheme}-u-hidden`);
                }
            };
            this.elements.countrySelect.addEventListener('change', syncState);
            syncState();
        }
    }
}

registerComponent(`.${componentName}`, FormContainer);
